import Axios from "axios";
import Cookies from "js-cookie";

const accessTokenCookieName = () =>
  window.__env.AUTH_TOKEN_NAME || "coaching-master-token";
const refreshTokenCookieName = () =>
  window.__env.AUTH_REFRESH_TOKEN_NAME || "coaching-master-refresh-token";

const client = Axios.create();

// Public account-activation / password-reset flows. These are driven by the
// reset token in the URL, NOT by an app session — the person clicking the email
// link may be logged out, or (when testing) be an admin whose access token has
// since expired. If we attach that stale Authorization header, the global
// `deserializeUser` middleware default-denies it with a 401 and the reset page
// gets bounced to login. So we neither send app-auth on these requests nor let
// their 401/403 trigger the login redirect. (Note: the admin-only
// `re-issue-temporary-password` endpoint is intentionally NOT matched here — it
// keeps normal auth.)
const isPublicTokenFlow = (url?: string): boolean =>
  !!url &&
  (url.includes("verify/user") ||
    url.includes("verify/re-issue-password") ||
    url.includes("verify-user") ||
    url.includes("reset-password"));

client.interceptors.request.use(
  (config) => {
    if (config.url?.includes("login") || isPublicTokenFlow(config.url)) {
      return config;
    }

    const token = Cookies.get(accessTokenCookieName());
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // SEC-09: BE's deserializeUser middleware reads the refresh token from the
    // `x-refresh` request header. Without it, an expired access token cannot
    // trigger rotation server-side and the user is bounced to login instead.
    const refresh = Cookies.get(refreshTokenCookieName());
    if (refresh) config.headers["x-refresh"] = refresh;

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// SEC-09: BE rotates both access and refresh tokens on every refresh and emits
// the new pair via response headers. Persist them before returning the response
// so subsequent requests use the rotated access token; otherwise the next
// refresh will 401 because the old session has been invalidated server-side.
const persistRotatedTokens = (headers: Record<string, any> | undefined) => {
  if (!headers) return;
  const newAccess = headers["x-access-token"];
  const newRefresh = headers["x-refresh-token"];
  if (newAccess) Cookies.set(accessTokenCookieName(), newAccess);
  if (newRefresh) Cookies.set(refreshTokenCookieName(), newRefresh);
};

client.interceptors.response.use(
  (res) => {
    persistRotatedTokens(res.headers as Record<string, any>);
    return res;
  },
  (err) => {
    const res = err.response;
    if (res) {
      persistRotatedTokens(res.headers as Record<string, any>);
      if (
        !res.config.url.includes("login") &&
        !res.config.url.includes("token") &&
        !isPublicTokenFlow(res.config.url) &&
        [401, 403].includes(res.status)
      ) {
        Cookies.remove(accessTokenCookieName());
        Cookies.remove(refreshTokenCookieName());
        window.location.href = "/";
      }
    }
    return Promise.reject(err);
  }
);

export default client;
